import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion';

/**
 * Renders a single service section (title, benefits, image, literature accordion).
 * Layout alternates left/right when `flipped` is true.
 */
const ServiceSection = ({ service, flipped = false }) => {
  return (
    <div id={service.id} className="scroll-mt-20">
      <div className={`grid md:grid-cols-2 gap-12 items-start ${flipped ? 'md:grid-flow-dense' : ''}`}>
        <div className={flipped ? 'md:col-start-2' : ''}>
          <div className="w-20 h-20 bg-emerald-100 rounded-lg flex items-center justify-center mb-6 text-emerald-600">
            {service.icon}
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">{service.title}</h2>
          <p className="text-lg text-slate-600 mb-6">{service.longDescription}</p>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Key Benefits</h3>
            <ul className="space-y-3">
              {service.benefits.map((benefit) => (
                <li key={benefit} className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <Link to="/contact">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {service.cta}
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>

        <div className={flipped ? 'md:col-start-1 md:row-start-1' : ''}>
          <img
            src={service.image}
            alt={service.title}
            className="rounded-lg shadow-2xl w-full h-96 object-cover mb-6"
            loading="lazy"
          />

          <Card className="border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl">
                <FileText className="w-6 h-6 mr-2 text-emerald-600" />
                Research &amp; Literature
              </CardTitle>
              <CardDescription>Evidence-based insights and industry best practices</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {service.literature.map((lit) => (
                  <AccordionItem key={lit.title} value={`item-${lit.title}`}>
                    <AccordionTrigger className="text-left font-semibold">
                      {lit.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-slate-600 mb-4">{lit.content}</p>
                      <a
                        href={lit.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-semibold"
                      >
                        Read More
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServiceSection;
